import { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, Container, Typography, Alert, Chip } from '@mui/material';
import { RefreshOutlined, Home, ReportProblem } from '@mui/icons-material';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  errorId: string;
}

export class GlobalErrorBoundary extends Component<Props, State> {
  private retryTimeoutId: number | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      errorId: '',
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Generate a unique error ID for tracking
    const errorId = `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    return {
      hasError: true,
      error,
      errorId,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to console in development
    if (import.meta.env.DEV) {
      console.group('🚨 Error Boundary Caught an Error');
      console.error('Error:', error);
      console.error('Error Info:', errorInfo);
      console.error('Component Stack:', errorInfo.componentStack);
      console.groupEnd();
    }

    this.setState({
      error,
      errorInfo,
    });

    // In production, you would send this to your error monitoring service
    // Example: Sentry.captureException(error, { contexts: { react: errorInfo } });
  }

  componentWillUnmount() {
    if (this.retryTimeoutId) {
      window.clearTimeout(this.retryTimeoutId);
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: undefined,
      errorInfo: undefined,
      errorId: '',
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleReportError = () => {
    const { error, errorInfo, errorId } = this.state;
    const errorReport = {
      errorId,
      message: error?.message,
      stack: error?.stack,
      componentStack: errorInfo?.componentStack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    };

    // Copy error report to clipboard
    navigator.clipboard
      .writeText(JSON.stringify(errorReport, null, 2))
      .then(() => {
        window.alert(
          'Error report copied to clipboard. Please share with support.'
        );
      })
      .catch(() => {
        console.log('Error Report:', errorReport);
        window.alert(
          'Error report logged to console. Please share with support.'
        );
      });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { error, errorId } = this.state;
      const isDevelopment = import.meta.env.DEV;

      return (
        <Container maxWidth="md" sx={{ py: 8 }}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            textAlign="center"
            gap={3}
          >
            <ReportProblem color="error" sx={{ fontSize: 80 }} />

            <Typography variant="h4" component="h1" color="error">
              Oops! Something went wrong
            </Typography>

            <Typography variant="body1" color="text.secondary" maxWidth="600px">
              We encountered an unexpected error. This has been logged and our
              team has been notified. Please try refreshing the page or contact
              support if the problem persists.
            </Typography>

            {errorId && (
              <Chip
                label={`Error ID: ${errorId}`}
                size="small"
                variant="outlined"
                color="error"
              />
            )}

            {isDevelopment && error && (
              <Alert
                severity="error"
                sx={{
                  textAlign: 'left',
                  width: '100%',
                  '& .MuiAlert-message': {
                    width: '100%',
                  },
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Development Error Details:
                </Typography>
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    overflow: 'auto',
                    maxHeight: '200px',
                  }}
                >
                  {error.message}
                  {'\n\n'}
                  {error.stack}
                </Typography>
              </Alert>
            )}

            <Box display="flex" gap={2} flexWrap="wrap" justifyContent="center">
              <Button
                variant="contained"
                startIcon={<RefreshOutlined />}
                onClick={this.handleRetry}
                color="primary"
              >
                Try Again
              </Button>

              <Button
                variant="outlined"
                startIcon={<RefreshOutlined />}
                onClick={this.handleReload}
              >
                Reload Page
              </Button>

              <Button
                variant="outlined"
                startIcon={<Home />}
                onClick={this.handleGoHome}
              >
                Go Home
              </Button>

              {isDevelopment && (
                <Button
                  variant="text"
                  onClick={this.handleReportError}
                  size="small"
                >
                  Copy Error Report
                </Button>
              )}
            </Box>

            <Typography variant="caption" color="text.secondary">
              If this problem continues, please contact our support team with
              the error ID above.
            </Typography>
          </Box>
        </Container>
      );
    }

    return this.props.children;
  }
}
